<template>
  <div class="opc-console">
    <!-- 未登录 -->
    <section v-if="!loggedIn" class="login-screen">
      <div class="login-card">
        <img src="../../assets/qingchuang-opc-logo.png" alt="轻创" />
        <span>FIELD CLUB / OPC</span>
        <h1>轻创 OPC 运营后台</h1>
        <p>活动、交易、增长与财务统一运营。</p>
        <el-input v-model="login.username" placeholder="管理员账号" @keyup.enter.native="doLogin" />
        <el-input v-model="login.password" type="password" show-password placeholder="管理员密码" @keyup.enter.native="doLogin" />
        <el-button :loading="busy" class="lime-button" @click="doLogin">进入运营台</el-button>
        <small>使用已配置的 OPC 管理员账号登录</small>
      </div>
    </section>

    <div v-else class="console-shell">
      <aside class="sidebar">
        <div class="brand">
          <img src="../../assets/qingchuang-opc-logo.png" alt="轻创" />
          <div><b>OPC 运营后台</b><small>FIELD CLUB / V1.0</small></div>
        </div>
        <nav>
          <template v-for="group in navigation">
            <p :key="group.label">{{ group.label }}</p>
            <button
              v-for="item in group.items"
              :key="item.id"
              :class="{ active: page === item.id }"
              @click="page = item.id"
            >
              <i>{{ item.icon }}</i>{{ item.label }}
            </button>
          </template>
        </nav>
        <div class="side-user">
          <b>{{ adminName }}</b>
          <small>实时接口模式 · OPC 专用</small>
        </div>
      </aside>

      <main class="workspace">
        <header class="topbar">
          <div class="crumb"><b>{{ pageTitle }}</b> / 轻创 OPC</div>
          <div class="top-actions">
            <span class="role-button">{{ adminName }}</span>
            <span class="avatar">{{ adminInitial }}</span>
            <button class="role-button" @click="logout">退出</button>
          </div>
        </header>

        <section class="content">
          <!-- 综合看板 -->
          <template v-if="page === 'dashboard'">
            <div class="page-head">
              <div><h1>综合经营大盘</h1><p>今天的现场待办与近 7 天经营表现</p></div>
              <button class="primary-action" @click="openActivity()">＋ 新建活动</button>
            </div>
            <div class="metrics">
              <article class="metric dark"><span>支付成交额</span><strong>¥{{ money(stats.paidAmount) }}</strong><small>已支付订单实时汇总</small></article>
              <article class="metric"><span>已支付报名</span><strong>{{ stats.paidOrderCount || 0 }}</strong><small>总订单 {{ stats.orderCount || 0 }}</small></article>
              <article class="metric"><span>用户规模</span><strong>{{ stats.userCount || 0 }}</strong><small>累计 OPC 用户</small></article>
              <article class="metric"><span>冻结中佣金</span><strong>¥{{ frozenCommission }}</strong><small>支付后 T+7 解冻</small></article>
            </div>
            <div class="dashboard-grid">
              <article class="panel">
                <div class="panel-head">
                  <div><h2>成交趋势</h2><p>占位示意，未接后端趋势接口</p></div>
                  <div class="periods">
                    <button v-for="x in ['今日', '近7天', '近30天']" :key="x" :class="{ active: period === x }" @click="period = x">{{ x }}</button>
                  </div>
                </div>
                <div class="chart">
                  <div v-for="(bar, index) in trendBars" :key="index" class="bar-wrap">
                    <span class="bar" :style="{ height: bar + '%' }"></span>
                    <small>{{ ['一', '二', '三', '四', '五', '六', '日'][index] }}</small>
                  </div>
                </div>
              </article>
              <article class="panel">
                <div class="panel-head"><div><h2>今日待办</h2><p>优先处理现场与交易动作</p></div></div>
                <button class="todo" @click="page = 'checkin'">
                  <span>✓</span><div><b>现场签到</b><small>查看已支付报名名单</small></div><em>{{ paidOrders.length }}</em>
                </button>
                <button class="todo" @click="page = 'refunds'">
                  <span>↩</span><div><b>退款审核</b><small>等待审核的退款申请</small></div><em>{{ pendingRefunds.length }}</em>
                </button>
                <button class="todo" @click="page = 'finance'">
                  <span>¥</span><div><b>提现登记</b><small>主理人待打款申请</small></div><em>{{ pendingWithdraws.length }}</em>
                </button>
              </article>
            </div>
            <div class="lower-grid">
              <article class="event-hero">
                <img v-if="activities[0] && activities[0].cover" :src="activities[0].cover" />
                <div>
                  <label>现场活动</label>
                  <h2>{{ activities[0] ? activities[0].title : '等待发布首场活动' }}</h2>
                  <p>报名、签到与分享数据在此同步。</p>
                </div>
              </article>
              <article class="panel task-panel">
                <h2>运营进度</h2>
                <div>
                  <b>{{ activities.length }}</b><small>活动管理</small>
                  <b>{{ referrals.length }}</b><small>分享线索</small>
                  <b>{{ commissions.length }}</b><small>佣金记录</small>
                </div>
              </article>
            </div>
          </template>

          <!-- 内容运营：后端无接口，不再展示占位数据 -->
          <template v-else-if="page === 'content'">
            <div class="page-head">
              <div><h1>内容运营</h1><p>首页 Banner、精选活动、共创笔记和成员推荐</p></div>
              <button class="primary-action" @click="$message.info('内容接口尚未落地，暂不能发布')">＋ 发布内容</button>
            </div>
            <article class="panel empty-panel">
              <h2>内容模块尚未接入</h2>
              <p>后端没有内容运营接口，因此这里不再渲染占位条目，以免把假数据当成已发布内容。等 OPC 发布模块落地后接真实接口。</p>
            </article>
          </template>

          <!-- 活动中心 -->
          <template v-else-if="page === 'activities'">
            <div class="page-head">
              <div><h1>活动中心</h1><p>发布活动、指定主理人、置顶与查看报名名单</p></div>
              <button class="primary-action" @click="openActivity()">＋ 新建活动</button>
            </div>
            <div class="filters">
              <button :class="{ active: statusFilter === null }" @click="setFilter(null)">全部</button>
              <button :class="{ active: statusFilter === 1 }" @click="setFilter(1)">已发布</button>
              <button :class="{ active: statusFilter === 0 }" @click="setFilter(0)">草稿 / 下架</button>
            </div>
            <div class="ticket-grid">
              <article v-for="item in activities" :key="item.id" class="ticket">
                <img v-if="item.cover" :src="item.cover" />
                <div class="ticket-copy">
                  <span :class="['tag', item.status === 1 ? 'published' : 'draft']">{{ item.status === 1 ? '已发布' : '草稿' }}</span>
                  <span v-if="item.isTop === 1" class="tag top-tag">置顶</span>
                  <h2>{{ item.title }}</h2>
                  <p>{{ item.location || '地点待定' }} · {{ item.startTime || '时间待定' }}</p>
                  <p>主理人：{{ hostLabel(item) }}</p>
                  <footer>
                    <span>{{ item.signedCount || 0 }} / {{ item.maxCount || '不限' }} 人</span>
                    <span class="ticket-actions">
                      <button @click="openActivity(item)">编辑 ›</button>
                      <button @click="showAttendees(item)">报名名单 ›</button>
                      <button @click="setPublished(item, item.status !== 1)">{{ item.status === 1 ? '下架' : '发布' }}</button>
                      <button @click="setTop(item, item.isTop !== 1)">{{ item.isTop === 1 ? '取消置顶' : '置顶' }}</button>
                    </span>
                  </footer>
                </div>
              </article>
            </div>
            <article class="panel categories">
              <div class="panel-head">
                <div><h2>活动专场</h2><p>控制小程序分类与排序</p></div>
                <button class="text-action" @click="addCategory">＋ 新建专场</button>
              </div>
              <div class="category-row">
                <span v-for="item in categories" :key="item.id">
                  {{ item.name }} <small>#{{ item.sort }}</small>
                  <button class="inline-action" @click="toggleCategory(item)">{{ item.status === 1 ? '停用' : '启用' }}</button>
                </span>
              </div>
            </article>
          </template>

          <!-- 现场签到 -->
          <template v-else-if="page === 'checkin'">
            <div class="page-head">
              <div><h1>现场签到</h1><p>已支付报名名单与核销状态</p></div>
              <button class="primary-action" @click="openScan">打开扫码核销</button>
            </div>
            <article class="panel">
              <el-table :data="paidOrders" size="small">
                <el-table-column prop="orderNo" label="订单号" min-width="180" />
                <el-table-column prop="userId" label="用户" width="90" />
                <el-table-column prop="activityId" label="活动" width="80" />
                <el-table-column label="实付" width="100"><template slot-scope="scope">¥{{ money(scope.row.amount) }}</template></el-table-column>
                <el-table-column prop="status" label="状态" width="120" />
                <el-table-column prop="paidAt" label="支付时间" min-width="160" />
              </el-table>
            </article>
          </template>

          <!-- 交易 / 退款 / 提现 -->
          <template v-else-if="page === 'orders' || page === 'refunds' || page === 'finance'">
            <div class="page-head">
              <div>
                <h1>{{ pageTitle }}</h1>
                <p>{{ page === 'orders' ? '筛选活动订单、支付状态与报名信息' : page === 'refunds' ? '审核退款申请并追踪原路退款' : '登记人工打款并保留处理流水' }}</p>
              </div>
            </div>
            <article class="panel table-panel">
              <el-table v-if="page === 'orders'" :data="orders" size="small">
                <el-table-column prop="orderNo" label="订单号" min-width="180" />
                <el-table-column prop="userId" label="用户" width="85" />
                <el-table-column prop="activityId" label="活动" width="75" />
                <el-table-column label="金额" width="100"><template slot-scope="scope">¥{{ money(scope.row.amount) }}</template></el-table-column>
                <el-table-column prop="status" label="状态" width="120" />
                <el-table-column prop="createdAt" label="下单时间" min-width="160" />
              </el-table>
              <el-table v-else-if="page === 'refunds'" :data="refunds" size="small">
                <el-table-column prop="orderNo" label="订单号" min-width="170" />
                <el-table-column prop="userId" label="用户" width="85" />
                <el-table-column prop="activityId" label="活动" width="75" />
                <el-table-column label="金额" width="95"><template slot-scope="scope">¥{{ money(scope.row.amount) }}</template></el-table-column>
                <el-table-column prop="refundReason" label="退款原因" min-width="160" />
                <el-table-column prop="status" label="状态" width="110" />
                <el-table-column label="操作" width="150">
                  <template slot-scope="scope">
                    <button class="inline-action" @click="approveRefund(scope.row)">通过退款</button>
                    <button class="inline-action danger" @click="rejectRefund(scope.row)">驳回</button>
                  </template>
                </el-table-column>
              </el-table>
              <el-table v-else :data="withdraws" size="small">
                <el-table-column prop="withdrawNo" label="提现单号" min-width="180" />
                <el-table-column prop="userId" label="主理人" width="90" />
                <el-table-column label="金额" width="100"><template slot-scope="scope">¥{{ money(scope.row.amount) }}</template></el-table-column>
                <el-table-column prop="payAccount" label="收款信息" min-width="160" />
                <el-table-column prop="status" label="状态" width="100" />
                <el-table-column prop="transferReference" label="打款流水" min-width="120" />
                <el-table-column label="操作" width="130">
                  <template slot-scope="scope">
                    <button v-if="scope.row.status === 0" class="inline-action" @click="approveWithdraw(scope.row)">登记打款</button>
                    <button v-if="scope.row.status === 0" class="inline-action danger" @click="rejectWithdraw(scope.row)">驳回</button>
                  </template>
                </el-table-column>
              </el-table>
            </article>
          </template>

          <!-- 增长 / 佣金 -->
          <template v-else-if="page === 'growth' || page === 'commissions'">
            <div class="page-head">
              <div>
                <h1>{{ pageTitle }}</h1>
                <p>{{ page === 'growth' ? '查看主理人、分享访问与报名转化' : '配置直推分佣并管理佣金明细' }}</p>
              </div>
              <button v-if="page === 'commissions'" class="primary-action" @click="saveRate">保存比例 {{ commissionRate }}</button>
            </div>
            <div v-if="page === 'growth'" class="metrics compact">
              <article class="metric dark"><span>分享线索</span><strong>{{ referrals.length }}</strong></article>
              <article class="metric"><span>主理人</span><strong>{{ hosts.length }}</strong></article>
              <article class="metric"><span>转化订单</span><strong>{{ convertedReferrals }}</strong></article>
            </div>
            <article class="panel table-panel">
              <div v-if="page === 'commissions'" class="rule">
                <span>直推佣金比例</span>
                <el-input v-model="commissionRate" size="small" />
                <em>0.10 = 实付金额的 10%</em>
              </div>
              <el-table v-if="page === 'growth'" :data="referrals" size="small">
                <el-table-column prop="id" label="线索" width="80" />
                <el-table-column prop="userId" label="报名用户" width="110" />
                <el-table-column prop="referrerId" label="引荐主理人" width="120" />
                <el-table-column prop="activityId" label="活动" width="80" />
                <el-table-column prop="orderId" label="订单" width="80" />
                <el-table-column label="状态"><template slot-scope="scope">{{ scope.row.status === 1 ? '已转化' : '点击 / 待支付' }}</template></el-table-column>
              </el-table>
              <el-table v-else :data="commissions" size="small">
                <el-table-column prop="orderId" label="订单" width="90" />
                <el-table-column prop="userId" label="获得人" width="100" />
                <el-table-column label="金额" width="100"><template slot-scope="scope">¥{{ money(scope.row.amount) }}</template></el-table-column>
                <el-table-column label="状态" width="110"><template slot-scope="scope">{{ commissionStatus(scope.row.status) }}</template></el-table-column>
                <el-table-column prop="settleTime" label="预计解冻" min-width="160" />
              </el-table>
            </article>
          </template>

          <!-- 用户与角色 -->
          <template v-else-if="page === 'users'">
            <div class="page-head">
              <div><h1>用户与角色</h1><p>授予主理人身份、查看管理员标记、调整佣金余额</p></div>
            </div>
            <article class="panel table-panel">
              <el-table :data="users" size="small">
                <el-table-column prop="id" label="ID" width="70" />
                <el-table-column prop="nickname" label="昵称" min-width="120" />
                <el-table-column prop="phone" label="手机号" width="130" />
                <el-table-column label="角色" width="150">
                  <template slot-scope="scope">
                    <el-select :value="scope.row.role" size="mini" @change="setRole(scope.row, $event)">
                      <el-option label="普通用户" :value="1" />
                      <el-option label="主理人" :value="2" />
                    </el-select>
                  </template>
                </el-table-column>
                <el-table-column label="后台管理员" width="110">
                  <template slot-scope="scope">{{ scope.row.isAdmin === 1 ? '是' : '否' }}</template>
                </el-table-column>
                <el-table-column label="佣金调整" width="100">
                  <template slot-scope="scope">
                    <button v-if="scope.row.role === 2" class="inline-action" @click="adjustCommission(scope.row)">调整</button>
                  </template>
                </el-table-column>
                <el-table-column label="用户标签" width="120">
                  <template slot-scope="scope">
                    <button class="inline-action" @click="showTags(scope.row)">查看</button>
                  </template>
                </el-table-column>
              </el-table>
            </article>
          </template>

          <!-- 操作记录 -->
          <template v-else>
            <div class="page-head">
              <div><h1>操作记录</h1><p>管理员的关键操作留痕</p></div>
            </div>
            <article class="panel table-panel">
              <el-table :data="auditLogs" size="small">
                <el-table-column prop="user_id" label="管理员" width="100" />
                <el-table-column prop="method" label="操作" width="100" />
                <el-table-column prop="path" label="接口" min-width="260" />
                <el-table-column prop="ip" label="IP" width="140" />
                <el-table-column prop="created_at" label="时间" min-width="160" />
              </el-table>
            </article>
          </template>
        </section>
      </main>

      <!-- 新建 / 编辑活动 -->
      <el-dialog :title="activityForm.id ? '编辑 OPC 活动' : '新建 OPC 活动'" :visible.sync="activityDialog" width="680px">
        <el-form :model="activityForm" label-width="110px" size="small">
          <el-form-item label="标题"><el-input v-model="activityForm.title" /></el-form-item>
          <el-form-item label="专场">
            <el-select v-model="activityForm.categoryId">
              <el-option v-for="item in categories" :key="item.id" :label="item.name" :value="item.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="封面 URL"><el-input v-model="activityForm.cover" /></el-form-item>
          <el-form-item label="活动介绍"><el-input v-model="activityForm.description" type="textarea" :rows="4" /></el-form-item>
          <el-form-item label="地点"><el-input v-model="activityForm.location" /></el-form-item>
          <el-form-item label="坐标">
            <el-input v-model="activityForm.longitude" placeholder="经度" />
            <el-input v-model="activityForm.latitude" placeholder="纬度" />
          </el-form-item>
          <el-form-item label="报名价"><el-input v-model="activityForm.price" /></el-form-item>
          <el-form-item label="茶饮费"><el-input v-model="activityForm.teaFee" /></el-form-item>
          <el-form-item label="名额上限"><el-input v-model="activityForm.maxCount" placeholder="0 表示不限" /></el-form-item>
          <el-form-item label="负责人用户ID">
            <el-input v-model="activityForm.hostUserId" placeholder="主理人用户 ID，可留空" />
          </el-form-item>
          <el-form-item label="开始时间"><el-input v-model="activityForm.startTime" placeholder="YYYY-MM-DD HH:mm:ss" /></el-form-item>
          <el-form-item label="结束时间"><el-input v-model="activityForm.endTime" placeholder="YYYY-MM-DD HH:mm:ss" /></el-form-item>
          <el-form-item label="发布"><el-switch v-model="activityForm.status" :active-value="1" :inactive-value="0" /></el-form-item>
        </el-form>
        <span slot="footer">
          <el-button @click="activityDialog = false">取消</el-button>
          <el-button type="primary" :loading="busy" @click="saveActivity">保存</el-button>
        </span>
      </el-dialog>

      <!-- 报名名单 -->
      <el-dialog title="活动报名名单" :visible.sync="attendeeDialog" width="760px">
        <el-table :data="attendees" size="small">
          <el-table-column prop="orderNo" label="订单号" min-width="200" />
          <el-table-column prop="userId" label="用户" width="90" />
          <el-table-column prop="amount" label="金额" width="100" />
          <el-table-column prop="status" label="状态" width="120" />
          <el-table-column prop="paidAt" label="支付时间" min-width="160" />
        </el-table>
      </el-dialog>

      <!-- 用户标签 -->
      <el-dialog title="用户标签" :visible.sync="tagDialog" width="520px">
        <el-table :data="userTags" size="small">
          <el-table-column prop="tagName" label="标签" min-width="140" />
          <el-table-column prop="source" label="来源" width="120" />
          <el-table-column prop="createdAt" label="时间" min-width="160" />
        </el-table>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import storage from "../../utils/storage";
import store from "../../store";

const emptyActivity = () => ({
  id: null,
  title: "",
  categoryId: null,
  cover: "",
  description: "",
  location: "",
  longitude: "",
  latitude: "",
  price: "0",
  teaFee: "0",
  maxCount: 0,
  hostUserId: "",
  startTime: "",
  endTime: "",
  status: 0,
  isTop: 0,
});

export default {
  name: "OpcConsole",
  data() {
    return {
      loggedIn: false,
      busy: false,
      login: { username: "", password: "" },
      adminUser: null,
      page: "dashboard",
      period: "近7天",
      stats: {},
      categories: [],
      activities: [],
      orders: [],
      refunds: [],
      withdraws: [],
      users: [],
      commissions: [],
      referrals: [],
      auditLogs: [],
      commissionRate: "0.10",
      statusFilter: null,
      activityDialog: false,
      activityForm: emptyActivity(),
      attendeeDialog: false,
      attendees: [],
      tagDialog: false,
      userTags: [],
      navigation: [
        { label: "工作台", items: [{ id: "dashboard", icon: "◫", label: "综合看板" }] },
        {
          label: "运营",
          items: [
            { id: "content", icon: "▧", label: "内容运营" },
            { id: "activities", icon: "▣", label: "活动中心" },
            { id: "checkin", icon: "✓", label: "现场签到" },
          ],
        },
        {
          label: "交易",
          items: [
            { id: "orders", icon: "□", label: "交易中心" },
            { id: "refunds", icon: "↩", label: "退款审核" },
          ],
        },
        { label: "增长", items: [{ id: "growth", icon: "↗", label: "增长中心" }] },
        {
          label: "财务",
          items: [
            { id: "finance", icon: "¥", label: "财务中心" },
            { id: "commissions", icon: "◎", label: "佣金规则" },
          ],
        },
        {
          label: "系统",
          items: [
            { id: "users", icon: "☰", label: "用户与角色" },
            { id: "system", icon: "⚙", label: "操作记录" },
          ],
        },
      ],
    };
  },
  computed: {
    pageTitle() {
      return {
        dashboard: "综合看板",
        content: "内容运营",
        activities: "活动中心",
        checkin: "现场签到",
        orders: "交易中心",
        refunds: "退款审核",
        growth: "增长中心",
        finance: "财务中心",
        commissions: "佣金规则",
        users: "用户与角色",
        system: "操作记录",
      }[this.page];
    },
    adminName() {
      const u = this.adminUser || {};
      if (u.nickname) return u.nickname;
      return u.id ? "管理员 #" + u.id : "管理员";
    },
    adminInitial() {
      return String(this.adminName).trim().charAt(0) || "管";
    },
    paidOrders() {
      return this.orders.filter((x) => String(x.status).includes("支付") || x.status === 1);
    },
    pendingRefunds() {
      return this.refunds.filter((x) => String(x.status).includes("待") || x.status === 0);
    },
    pendingWithdraws() {
      return this.withdraws.filter((x) => x.status === 0 || String(x.status).includes("待"));
    },
    frozenCommission() {
      return this.money(
        this.commissions.filter((x) => x.status === 0).reduce((s, x) => s + Number(x.amount || 0), 0)
      );
    },
    hosts() {
      return this.users.filter((x) => x.role === 2);
    },
    convertedReferrals() {
      return this.referrals.filter((x) => x.status === 1).length;
    },
    // 占位示意：后端没有成交趋势接口，面板上已标注「占位示意」，不要当成真实数据。
    trendBars() {
      return this.period === "今日"
        ? [42, 60, 35, 73, 58, 85, 67]
        : this.period === "近30天"
        ? [28, 49, 64, 58, 75, 82, 91]
        : [37, 52, 44, 68, 59, 82, 73];
    },
  },
  created() {
    const token = storage.get("opc_admin_token");
    if (token) {
      this.loggedIn = true;
      this.adminUser = storage.get("opc_admin_user") || null;
      store.commit("setToken", token);
      this.loadAll();
    }
  },
  methods: {
    unwrap(promise) {
      return promise.then((r) => r.data.data);
    },
    async doLogin() {
      this.busy = true;
      try {
        const res = await this.$http.post("/admin/auth/login", this.login);
        const payload = res.data.data;
        storage.set("opc_admin_token", payload.token);
        storage.set("opc_admin_user", payload.user);
        store.commit("setToken", payload.token);
        this.adminUser = payload.user || null;
        this.loggedIn = true;
        await this.loadAll();
      } catch (e) {
        this.$message.error((e && e.msg) || "管理端登录失败");
      } finally {
        this.busy = false;
      }
    },
    logout() {
      // delToken 会一并清掉 opc_admin_token 与 opc_admin_user（见 store/index.js）
      store.commit("delToken");
      this.loggedIn = false;
      this.adminUser = null;
      this.page = "dashboard";
    },
    async loadAll() {
      try {
        const [stats, categories, activities, orders, refunds, withdraws, users, commissions, rate, referrals, auditLogs] = await Promise.all([
          this.unwrap(this.$http.get("/admin/stats/dashboard")),
          this.unwrap(this.$http.get("/admin/categories")),
          this.unwrap(this.$http.get("/admin/activities")),
          this.unwrap(this.$http.get("/admin/orders")),
          this.unwrap(this.$http.get("/admin/refunds")),
          this.unwrap(this.$http.get("/admin/withdraw")),
          this.unwrap(this.$http.get("/admin/users")),
          this.unwrap(this.$http.get("/admin/commissions")),
          this.unwrap(this.$http.get("/admin/commission/rule")),
          this.unwrap(this.$http.get("/admin/referrals")),
          this.unwrap(this.$http.get("/admin/audit-logs")),
        ]);
        this.stats = stats || {};
        this.categories = categories || [];
        this.activities = activities || [];
        this.orders = orders || [];
        this.refunds = refunds || [];
        this.withdraws = withdraws || [];
        this.users = users || [];
        this.commissions = commissions || [];
        this.referrals = referrals || [];
        this.auditLogs = auditLogs || [];
        this.commissionRate = String(rate || "0.10");
      } catch (e) {
        this.$message.error((e && e.msg) || "加载 OPC 数据失败，请重新登录或检查服务端");
      }
    },
    money(value) {
      return Number(value || 0).toFixed(2);
    },
    commissionStatus(status) {
      return { 0: "冻结中", 1: "可提现", 2: "已提现", 3: "已扣回" }[status] || status;
    },
    hostLabel(item) {
      return item.hostUserId ? "用户 #" + item.hostUserId : "未指定";
    },
    openScan() {
      this.$message.info("扫码核销在小程序端完成，后台只查看已支付名单");
    },

    // ── 活动 ──────────────────────────────────────────────
    setFilter(value) {
      this.statusFilter = value;
      this.loadActivities();
    },
    async loadActivities() {
      try {
        const list = await this.unwrap(this.$http.get("/admin/activities", { params: { status: this.statusFilter } }));
        this.activities = list || [];
      } catch (e) {
        this.$message.error((e && e.msg) || "活动列表加载失败");
      }
    },
    openActivity(row) {
      this.activityForm = row ? { ...row } : emptyActivity();
      this.activityDialog = true;
    },
    async saveActivity() {
      this.busy = true;
      try {
        const form = this.activityForm;
        const data = {
          ...form,
          categoryId: Number(form.categoryId),
          price: Number(form.price),
          teaFee: Number(form.teaFee),
          maxCount: Number(form.maxCount || 0),
          longitude: form.longitude === "" || form.longitude === null ? null : Number(form.longitude),
          latitude: form.latitude === "" || form.latitude === null ? null : Number(form.latitude),
          hostUserId: form.hostUserId ? Number(form.hostUserId) : null,
        };
        if (data.id) await this.$http.put("/admin/activities/" + data.id, data);
        else await this.$http.post("/admin/activities", data);
        this.activityDialog = false;
        await this.loadAll();
        this.$message.success("活动已保存");
      } catch (e) {
        this.$message.error((e && e.msg) || "活动保存失败");
      } finally {
        this.busy = false;
      }
    },
    setPublished(row, value) {
      // 后端 update 语句不写 is_top，所以整行回传不会覆盖置顶状态。
      this.$http
        .put("/admin/activities/" + row.id, { ...row, status: value ? 1 : 0 })
        .then(() => this.loadAll())
        .catch((e) => this.$message.error((e && e.msg) || "发布状态更新失败"));
    },
    setTop(row, value) {
      this.$http
        .post("/admin/activities/" + row.id + "/top?enabled=" + value)
        .then(() => this.loadAll())
        .catch((e) => this.$message.error((e && e.msg) || "置顶更新失败"));
    },
    async showAttendees(row) {
      try {
        this.attendees = (await this.unwrap(this.$http.get("/admin/activities/" + row.id + "/attendees"))) || [];
        this.attendeeDialog = true;
      } catch (e) {
        this.$message.error((e && e.msg) || "报名名单加载失败");
      }
    },

    // ── 专场 ──────────────────────────────────────────────
    addCategory() {
      this.$prompt("请输入专场名称", "新建专场", { confirmButtonText: "创建", cancelButtonText: "取消" })
        .then(({ value }) => this.$http.post("/admin/categories", { name: value, sort: this.categories.length + 1, status: 1 }))
        .then(() => this.loadAll())
        .catch((e) => {
          if (e && e.msg) this.$message.error(e.msg);
        });
    },
    toggleCategory(row) {
      this.$http
        .put("/admin/categories/" + row.id, { ...row, status: row.status === 1 ? 0 : 1 })
        .then(() => this.loadAll())
        .catch((e) => this.$message.error((e && e.msg) || "专场更新失败"));
    },

    // ── 退款 / 提现 ───────────────────────────────────────
    approveRefund(row) {
      this.$confirm("确认审核通过并发起微信原路退款？", "退款审核")
        .then(() => this.$http.post("/admin/refunds/" + row.id + "/approve"))
        .then((r) => {
          this.$message.success((r && r.data && r.data.msg) || "已提交退款");
          this.loadAll();
        })
        .catch((e) => {
          if (e && e.msg) this.$message.error(e.msg);
        });
    },
    rejectRefund(row) {
      this.$prompt("请填写驳回原因", "驳回退款申请")
        .then(({ value }) => this.$http.post("/admin/refunds/" + row.id + "/reject", { reason: value }))
        .then(() => this.loadAll())
        .catch((e) => {
          if (e && e.msg) this.$message.error(e.msg);
        });
    },
    approveWithdraw(row) {
      this.$prompt("完成线下转账后，填写银行/微信转账流水号", "登记打款")
        .then(({ value }) => this.$http.post("/admin/withdraw/" + row.id + "/approve", { transferReference: value }))
        .then(() => this.loadAll())
        .catch((e) => {
          if (e && e.msg) this.$message.error(e.msg);
        });
    },
    rejectWithdraw(row) {
      this.$prompt("请填写驳回原因", "驳回提现申请")
        .then(({ value }) => this.$http.post("/admin/withdraw/" + row.id + "/reject", { remark: value }))
        .then(() => this.loadAll())
        .catch((e) => {
          if (e && e.msg) this.$message.error(e.msg);
        });
    },

    // ── 用户 / 佣金 ───────────────────────────────────────
    setRole(row, role) {
      this.$http
        .post("/admin/users/" + row.id + "/role", { role })
        .then(() => this.loadAll())
        .catch((e) => this.$message.error((e && e.msg) || "角色更新失败"));
    },
    adjustCommission(row) {
      this.$prompt("正数增加、负数调减，最多两位小数", "调整主理人佣金", {
        inputPattern: /^-?\d+(\.\d{1,2})?$/,
        inputErrorMessage: "请输入有效金额",
      })
        .then(({ value: amount }) =>
          this.$prompt("请填写本次调整原因", "调整原因", { inputPattern: /\S+/, inputErrorMessage: "调整原因不能为空" }).then(
            ({ value: reason }) => this.$http.post("/admin/wallets/" + row.id + "/adjust", { amount, reason })
          )
        )
        .then(() => {
          this.$message.success("佣金余额已调整");
          this.loadAll();
        })
        .catch((e) => {
          if (e && e.msg) this.$message.error(e.msg);
        });
    },
    async showTags(row) {
      try {
        this.userTags = (await this.unwrap(this.$http.get("/admin/users/" + row.id + "/tags"))) || [];
        this.tagDialog = true;
      } catch (e) {
        this.$message.error((e && e.msg) || "用户标签加载失败");
      }
    },
    async saveRate() {
      const rate = Number(this.commissionRate);
      if (!Number.isFinite(rate) || rate < 0 || rate > 1) {
        return this.$message.error("比例需在 0 到 1 之间");
      }
      try {
        await this.$http.put("/admin/commission/rule", { rate });
        this.$message.success("佣金比例已更新");
      } catch (e) {
        this.$message.error((e && e.msg) || "保存佣金比例失败");
      }
    },
  },
};
</script>

<style scoped>
.opc-console{min-height:100vh;font-family:Inter,"PingFang SC","Microsoft YaHei",sans-serif;color:#080a07;background:#b9ec6d}.login-screen{display:grid;min-height:100vh;place-items:center;background:radial-gradient(circle at 50% 0,#f0ffda,#b9ec6d 70%)}.login-card{width:400px;padding:42px;border:1px solid rgba(8,10,7,.13);border-radius:25px;background:#f7faef;box-shadow:0 30px 70px rgba(28,57,13,.2)}.login-card img{width:88px;height:48px;object-fit:contain}.login-card>span,.login-card small{display:block;color:#67775d;font-size:11px}.login-card h1{margin:22px 0 5px;font-size:29px;letter-spacing:-1px}.login-card p{margin:0 0 22px;color:#66765d;font-size:13px}.login-card .el-input{margin-bottom:12px}.lime-button{width:100%;margin-top:8px;border:0;background:#080a07;color:#c9ff69;font-weight:800}.login-card small{margin-top:15px;text-align:center}.console-shell{display:grid;min-height:100vh;grid-template-columns:240px 1fr}.sidebar{position:fixed;z-index:5;width:240px;height:100vh;padding:28px 16px;background:#080a07;color:#c8d5c0}.brand{display:flex;align-items:center;gap:10px;padding:0 9px 28px;border-bottom:1px solid rgba(255,255,255,.13)}.brand img{width:67px;height:43px;object-fit:contain}.brand b{display:block;font-size:12px;letter-spacing:.6px}.brand small{display:block;margin-top:3px;color:#829077;font-size:10px}.sidebar nav>p{margin:22px 9px 8px;color:#728069;font-size:10px;font-weight:800;letter-spacing:1px}.sidebar nav button{position:relative;display:flex;width:100%;align-items:center;gap:11px;padding:10px;border:0;border-radius:10px;background:transparent;color:#b7c5ae;text-align:left;cursor:pointer}.sidebar nav button i{width:18px;color:#d8e6d0;font-size:16px;font-style:normal;text-align:center}.sidebar nav button.active{color:#c9ff69;font-weight:800}.sidebar nav button.active:before{position:absolute;left:0;width:3px;height:19px;border-radius:5px;background:#c9ff69;content:""}.sidebar nav button.active i{color:#c9ff69}.side-user{position:absolute;right:16px;bottom:20px;left:16px;padding:12px;border:1px solid rgba(201,255,105,.13);border-radius:13px;background:#111810}.side-user b,.side-user small{display:block}.side-user b{color:#c9ff69;font-size:11px}.side-user small{margin-top:4px;color:#8e9e85;font-size:10px}.workspace{grid-column:2;min-width:0;padding:0 34px 40px;background:radial-gradient(circle at 55% -20%,#efffd9 0,#d8f7ad 31%,#b9ec6d 78%)}.topbar{display:flex;height:78px;align-items:center;justify-content:space-between;border-bottom:1px solid rgba(8,10,7,.13)}.crumb{color:#526247;font-size:13px}.crumb b{margin-right:8px;color:#080a07;font-size:17px}.top-actions{display:flex;align-items:center;gap:12px}.search{width:260px;padding:9px 12px;border:1px solid rgba(8,10,7,.12);border-radius:10px;background:rgba(255,255,255,.35);color:#617058;font-size:12px}.role-button{padding:9px 11px;border:1px solid rgba(8,10,7,.12);border-radius:10px;background:#edf9cf;color:#080a07;font-size:12px;cursor:pointer}.avatar{display:grid;width:34px;height:34px;place-items:center;border-radius:50%;background:#080a07;color:#c9ff69;font-weight:800}.content{max-width:1440px;margin:0 auto;padding-top:29px}.page-head{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:23px}.page-head h1{margin:0;font-size:30px;letter-spacing:-1.4px}.page-head p{margin:5px 0 0;color:#54654b;font-size:12px}.primary-action{padding:10px 15px;border:0;border-radius:9px;background:#080a07;color:#c9ff69;font-size:12px;font-weight:800;cursor:pointer}.metrics{display:grid;grid-template-columns:repeat(4,1fr);gap:14px}.metric{min-height:118px;padding:17px;border:1px solid rgba(8,10,7,.1);border-radius:18px;background:rgba(249,255,238,.68)}.metric.dark{background:#080a07;color:#fff}.metric span{display:block;color:#617058;font-size:11px}.metric.dark span{color:#bdcbb4}.metric strong{display:block;margin-top:12px;font-size:28px;letter-spacing:-1px}.metric small{display:block;margin-top:6px;color:#609144;font-size:11px}.metric.dark small{color:#c9ff69}.dashboard-grid,.lower-grid{display:grid;grid-template-columns:1.35fr .85fr;gap:18px;margin-top:18px}.panel{overflow:hidden;border:1px solid rgba(8,10,7,.1);border-radius:18px;background:rgba(249,255,238,.76)}.panel-head{display:flex;align-items:center;justify-content:space-between;padding:18px 19px 0}.panel h2{margin:0;font-size:17px}.panel-head p{margin:3px 0 0;color:#687860;font-size:11px}.periods{display:flex;gap:4px;padding:4px;border-radius:9px;background:rgba(8,10,7,.08)}.periods button{padding:6px 8px;border:0;border-radius:6px;background:transparent;color:#53624c;font-size:10px;cursor:pointer}.periods .active{background:#080a07;color:#c9ff69;font-weight:800}.chart{display:flex;height:215px;align-items:end;gap:14px;padding:26px 24px 23px}.bar-wrap{display:flex;flex:1;height:100%;flex-direction:column;justify-content:end;gap:7px;text-align:center}.bar{min-height:11px;border-radius:7px 7px 2px 2px;background:linear-gradient(#c9ff69,#5d9930)}.bar-wrap small{color:#68785e;font-size:10px}.todo{display:flex;width:calc(100% - 36px);align-items:center;gap:10px;margin:0 18px;padding:13px 0;border:0;border-bottom:1px solid rgba(8,10,7,.12);background:transparent;text-align:left;cursor:pointer}.todo:last-child{border:0}.todo span{display:grid;width:28px;height:28px;place-items:center;border-radius:9px;background:#080a07;color:#c9ff69}.todo b,.todo small{display:block}.todo b{font-size:12px}.todo small{margin-top:2px;color:#6c7c64;font-size:10px}.todo em{margin-left:auto;padding:4px 7px;border-radius:20px;background:#c9ff69;font-size:10px;font-style:normal;font-weight:800}.event-hero{position:relative;min-height:180px;overflow:hidden;padding:19px;border-radius:18px;background:#080a07;color:#fff}.event-hero img{position:absolute;right:0;bottom:0;width:53%;height:100%;object-fit:cover;opacity:.62}.event-hero:after{position:absolute;inset:0;background:linear-gradient(90deg,#080a07 30%,rgba(8,10,7,.18));content:""}.event-hero div{position:relative;z-index:1;max-width:260px}.event-hero label{display:inline-block;padding:4px 7px;border-radius:6px;background:#c9ff69;color:#080a07;font-size:10px;font-weight:800}.event-hero h2{margin:11px 0 7px;font-size:21px;line-height:1.2}.event-hero p{margin:0;color:#c7d9bb;font-size:11px}.task-panel{padding:19px}.task-panel>div{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:18px}.task-panel b,.task-panel small{display:block}.task-panel b{font-size:21px}.task-panel small{color:#62725a;font-size:10px}.ticket-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}.ticket{position:relative;overflow:hidden;border-radius:17px;background:#f5f9ea}.ticket img{width:100%;height:122px;object-fit:cover}.ticket-copy{padding:14px}.tag{display:inline-block;padding:4px 7px;border-radius:7px;font-size:10px;font-weight:800}.published{background:#c9ff69}.draft{background:#e4e8df;color:#65745e}.ticket h2{margin:9px 0 5px;font-size:15px}.ticket p{margin:0;color:#63725a;font-size:10px}.ticket footer{display:flex;align-items:center;justify-content:space-between;margin-top:12px;font-size:11px}.ticket footer button,.text-action{border:0;background:transparent;color:#305e18;font-size:11px;font-weight:800;cursor:pointer}.categories{margin-top:18px;padding-bottom:18px}.category-row{display:flex;gap:9px;padding:18px}.category-row span{padding:8px 11px;border:1px solid rgba(8,10,7,.12);border-radius:10px;background:#edf9cf;font-size:12px}.category-row small{color:#708067}.filters{display:flex;align-items:center;gap:9px;margin-bottom:16px}.filters button{padding:9px 11px;border:1px solid rgba(8,10,7,.13);border-radius:9px;background:rgba(255,255,255,.45);font-size:12px;cursor:pointer}.filters .active{background:#080a07;color:#c9ff69}.filters div{margin-left:auto;padding:9px 12px;color:#617058;font-size:12px}.table-panel{padding:0}.table-panel ::v-deep .el-table{background:transparent}.table-panel ::v-deep .el-table th,.table-panel ::v-deep .el-table tr,.table-panel ::v-deep .el-table td{background:transparent}.table-panel ::v-deep .el-table th{background:rgba(8,10,7,.05);color:#64745c;font-size:10px}.table-panel ::v-deep .el-table td{border-bottom-color:rgba(8,10,7,.1)}.inline-action{margin-right:10px;padding:0;border:0;background:transparent;color:#315d19;font-size:11px;font-weight:800;cursor:pointer}.inline-action.danger{color:#b6402d}.compact{margin-bottom:18px}.rule{display:flex;align-items:center;gap:12px;padding:16px;border-bottom:1px solid rgba(8,10,7,.1);font-size:12px;font-weight:800}.rule .el-input{width:100px}.rule em{color:#64745c;font-size:11px;font-style:normal;font-weight:400}.content-cards{display:grid;grid-template-columns:repeat(4,1fr);gap:14px}.content-cards article{min-height:178px;padding:16px;border:1px solid rgba(8,10,7,.12);border-radius:17px;background:rgba(249,255,238,.76)}.content-cards span{display:inline-block;padding:4px 7px;border-radius:6px;background:#c9ff69;font-size:10px;font-weight:800}.content-cards h2{margin:15px 0 5px;font-size:15px}.content-cards p{margin:0;color:#65755c;font-size:11px}.content-cards footer{display:flex;justify-content:space-between;margin-top:24px}.content-cards footer b{color:#3e6b25;font-size:10px}.content-cards button{border:0;background:transparent;color:#315d19;font-size:11px;font-weight:800}.mini-preview{position:relative;margin-top:18px;padding:23px;overflow:hidden;border-radius:18px;background:#080a07;color:#fff}.mini-preview h2{margin:0;font-size:18px}.mini-preview p{color:#bed3b1}.mini-preview strong{display:inline-block;margin-top:10px;padding:7px 9px;border-radius:7px;background:#c9ff69;color:#080a07;font-size:11px}
/* 活动发布 / 置顶 / 报名名单 / 用户与角色等区块新增的样式 */
.top-tag{margin-left:5px;background:#080a07;color:#c9ff69}.ticket-actions{display:flex;flex-wrap:wrap;gap:9px;justify-content:flex-end}.empty-panel{padding:26px 22px}.empty-panel h2{margin:0 0 6px;font-size:17px}.empty-panel p{margin:0;color:#65755c;font-size:12px;line-height:1.7}
@media(max-width:1000px){.console-shell{grid-template-columns:1fr}.sidebar{position:static;width:auto;height:auto}.workspace{grid-column:1}.metrics,.ticket-grid,.content-cards{grid-template-columns:repeat(2,1fr)}.dashboard-grid,.lower-grid{grid-template-columns:1fr}.topbar,.workspace{padding-left:18px;padding-right:18px}.search{display:none}}
</style>
